<!-- Inline (NOT a fixed overlay): the shell sidebar/navbar stay visible. The root takes the
     viewport height left under the navbar + page padding so the columns can scroll internally. -->
<div
  class="flex h-[calc(100vh-7rem)] min-h-[540px] flex-col overflow-hidden rounded-xl border border-[#EAEAEA] dark:border-line bg-slate-50 dark:bg-surface-0">
  <!-- ── header bar ── -->
  <div
    class="flex shrink-0 items-center gap-3 border-b border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-5 py-3">
    <button type="button" (click)="closed.emit()"
      class="flex items-center gap-1.5 rounded-lg border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-0 px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-ink-soft transition-colors hover:bg-slate-100 dark:hover:bg-line">
      <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
        stroke-linejoin="round">
        <path d="M19 12H5" />
        <path d="M12 19l-7-7 7-7" />
      </svg>
      Back
    </button>
    @if (session(); as s) {
    <span
      class="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-50 text-sm font-semibold text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-300">
      {{ ((s.companyName || s.hostName || '?').charAt(0)).toUpperCase() }}
    </span>
    <div class="min-w-0">
      <div class="flex items-center gap-2">
        <h2 class="truncate text-base font-semibold text-[#141437] dark:text-white">{{ s.companyName || s.hostName ||
          'Session report' }}</h2>
        <span class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset"
          [class]="statusClass(s.status)">
          <span class="h-1.5 w-1.5 rounded-full" [class]="statusDot(s.status)"></span>{{ statusLabel(s.status) }}
        </span>
      </div>
      <div class="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400">
        <span class="tabular-nums">{{ dateRange() }}</span>
        <span>·</span>
        <span class="tabular-nums">{{ durationSecToStr(s.durationSec) }}</span>
        <span>·</span>
        <span>{{ outcomeLabel() }}</span>
      </div>
    </div>
    <div class="ml-auto flex items-center gap-2">
      <span class="hidden text-[10px] uppercase tracking-wider text-slate-400 md:block">Esc to close</span>
      <button type="button" (click)="copySessionId()" title="Copy full session id"
        class="rounded-lg border px-2.5 py-1 font-mono text-[11px] transition-all duration-150 active:scale-95"
        [class]="copied()
          ? 'border-emerald-300 bg-emerald-50 text-emerald-600 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
          : 'border-[#EAEAEA] text-slate-500 hover:border-slate-300 hover:bg-slate-100 dark:border-line dark:text-ink-soft/70 dark:hover:border-line-strong dark:hover:bg-line'">
        @if (copied()) { Copied ✓ } @else { {{ shortId(s.sessionId) }} ⧉ }
      </button>
    </div>
    } @else {
    <h2 class="text-base font-semibold text-[#141437] dark:text-white">Session report</h2>
    }
  </div>

  <!-- ── loading / error ── -->
  @if (loading()) {
  <div class="grid flex-1 place-items-center">
    <div class="flex flex-col items-center gap-3">
      <span class="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"></span>
      <p class="text-sm text-slate-400">Loading session report…</p>
    </div>
  </div>
  }
  @if (!loading() && error()) {
  <div class="grid flex-1 place-items-center">
    <p class="text-sm text-red-500">{{ error() }}</p>
  </div>
  }

  <!-- ── content: the report never scrolls as a whole — each column carries its own scroller (lg+) ── -->
  @if (!loading() && report(); as r) {
  <div
    class="scrollbar-theme flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden px-5 py-4 lg:overflow-y-hidden">

    <!-- stat tiles -->
    <div class="grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <div
        class="flex items-center gap-3 rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
        <span
          class="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500 dark:bg-indigo-500/10 dark:text-indigo-300">
          <svg class="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </span>
        <div>
          <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{
            stat('participants') }}</div>
          <div class="text-[11px] text-slate-400">Participants</div>
        </div>
      </div>
      <div
        class="flex items-center gap-3 rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
        <span
          class="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-500 dark:bg-sky-500/10 dark:text-sky-300">
          <svg class="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </span>
        <div>
          <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{ stat('turns') }}
          </div>
          <div class="text-[11px] text-slate-400">Transcript turns</div>
        </div>
      </div>
      <div
        class="flex items-center gap-3 rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
        <span
          class="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-500 dark:bg-violet-500/10 dark:text-violet-300">
          <svg class="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8" />
            <path d="M12 17v4" />
          </svg>
        </span>
        <div>
          <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{ stat('slides') }}
          </div>
          <div class="text-[11px] text-slate-400">Slides shown</div>
        </div>
      </div>
      <div
        class="flex items-center gap-3 rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
        <span
          class="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-500 dark:bg-amber-500/10 dark:text-amber-300">
          <svg class="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round">
            <path
              d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
          </svg>
        </span>
        <div>
          <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{ stat('tokens') }}
          </div>
          <div class="text-[11px] text-slate-400">Token mints</div>
        </div>
      </div>
      <div
        class="flex items-center gap-3 rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
        <span
          class="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-50 text-purple-500 dark:bg-purple-500/10 dark:text-purple-300">
          <svg class="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </span>
        <div>
          <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{ stat('admin') }}
          </div>
          <div class="text-[11px] text-slate-400">Actions</div>
        </div>
      </div>
      <div
        class="flex items-center gap-3 rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
        <span
          class="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 dark:bg-emerald-500/10 dark:text-emerald-300">
          <svg class="h-[18px] w-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round">
            <path d="M12 1v22" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
        </span>
        <div>
          @if (costs(); as c) {
          <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{ usd(c.totalUsd) }}
          </div>
          <div class="text-[11px] text-slate-400">Demo cost@if (c.anyEstimated) { <span class="text-amber-500"> ·
              est.</span> }</div>
          } @else {
          <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">—</div>
          <div class="text-[11px] text-slate-400">Demo cost</div>
          }
        </div>
      </div>
    </div>

  <div class="grid min-h-0 grid-cols-1 gap-4 lg:flex-1 lg:grid-cols-[330px_1fr]">
    <!-- ── left rail: overview / participants / security (own scroller on lg) ── -->
    <div class="scrollbar-theme space-y-4 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
      @if (session(); as s) {
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
        <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Overview</h3>
        <dl class="space-y-1.5 text-sm">
          <div class="flex justify-between gap-3">
            <dt class="text-slate-400">Kind</dt>
            <dd class="capitalize text-slate-700 dark:text-ink-soft">{{ s.kind }}</dd>
          </div>
          <div class="flex justify-between gap-3">
            <dt class="text-slate-400">AI agent</dt>
            <dd class="text-slate-700 dark:text-ink-soft">{{ s.aiAgentName || 'AI' }}</dd>
          </div>
          <div class="flex justify-between gap-3">
            <dt class="text-slate-400">Model</dt>
            <dd class="truncate text-right text-slate-700 dark:text-ink-soft">{{ s.model || '—' }}</dd>
          </div>
          <div class="flex justify-between gap-3">
            <dt class="text-slate-400">Transcript</dt>
            <dd class="text-slate-700 dark:text-ink-soft">{{ s.transcriptStatus || 'none' }}</dd>
          </div>
          <div class="flex justify-between gap-3">
            <dt class="text-slate-400">Call record</dt>
            <dd class="text-slate-700 dark:text-ink-soft">{{ s.callId ? ('#' + s.callId) : '—' }}</dd>
          </div>
          @if (r.linkedSessionIds.length) {
          <div class="flex justify-between gap-3">
            <dt class="text-slate-400">Linked room</dt>
            <dd class="font-mono text-xs text-slate-500 dark:text-ink-soft/60">{{ shortId(r.linkedSessionIds[0]) }}
            </dd>
          </div>
          }
        </dl>
        <div class="mt-3 space-y-1 border-t border-[#EAEAEA] dark:border-line pt-2.5 text-[11px]">
          <div class="flex justify-between gap-3"><span class="text-slate-400">Started</span><span
              class="tabular-nums text-slate-600 dark:text-ink-soft/80">{{ timeOf(s.startedAt) }}</span></div>
          <div class="flex justify-between gap-3"><span class="text-slate-400">Ended</span><span
              class="tabular-nums text-slate-600 dark:text-ink-soft/80">{{ timeOf(s.endedAt) }}</span></div>
          <div class="flex justify-between gap-3"><span class="text-slate-400">Duration</span><span
              class="tabular-nums text-slate-600 dark:text-ink-soft/80">{{ durationSecToStr(s.durationSec) }}</span>
          </div>
        </div>
      </div>

      @if (userRows().length) {
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
        <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">User details</h3>
        <dl class="space-y-1.5 text-sm">
          @for (row of userRows(); track row.label) {
          <div class="flex justify-between gap-3">
            <dt class="shrink-0 text-slate-400">{{ row.label }}</dt>
            @if (row.href) {
            <dd class="min-w-0 truncate text-right">
              <a [href]="row.href" [target]="row.label === 'Website' ? '_blank' : null" rel="noopener"
                class="text-indigo-600 hover:underline dark:text-indigo-300" [title]="row.value">{{ row.value }}</a>
            </dd>
            } @else {
            <dd class="min-w-0 truncate text-right text-slate-700 dark:text-ink-soft" [title]="row.value">{{ row.value
              }}</dd>
            }
          </div>
          }
        </dl>
      </div>
      }

      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
        <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Participants</h3>
        <div class="space-y-2.5">
          @for (p of r.participants; track p.participantId) {
          <div class="flex items-center gap-2.5">
            <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
              [class]="roleBadge(p.role)">{{ (p.name || '?').charAt(0).toUpperCase() }}</span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <!-- A caller nobody has named yet IS their number (the PIN gate is the
                     only moment it is knowable) — render it as the dialable thing it is
                     rather than as an inert string an admin has to retype. Once the agent
                     asks who is calling, the name takes this slot and the number moves to
                     the line below (see identify_caller). -->
                @if (p.phone && p.phone === p.name) {
                <a [href]="'tel:' + p.phone"
                  class="truncate text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-300">{{ p.name
                  }}</a>
                } @else {
                <span class="truncate text-sm font-medium text-slate-800 dark:text-ink-soft">{{ p.name }}</span>
                }
                <span class="rounded px-1 py-px text-[9px] font-semibold uppercase" [class]="roleBadge(p.role)">{{
                  p.role }}</span>
                @if (p.via === 'phone') {
                <span
                  class="shrink-0 rounded px-1 py-px text-[9px] font-semibold uppercase bg-teal-50 text-teal-700 dark:bg-teal-400/10 dark:text-teal-300">phone</span>
                }
              </div>
              <!-- The host who joined by phone: name above, the line they were on here. -->
              @if (p.phone && p.phone !== p.name) {
              <a [href]="'tel:' + p.phone" class="text-[11px] text-indigo-600 hover:underline dark:text-indigo-300">{{
                p.phone }}</a>
              }
              <div class="text-[11px] tabular-nums text-slate-400">
                {{ participantWindow(p) }}
                @if (participantDuration(p); as dur) { <span class="text-slate-500 dark:text-ink-soft/50"> · {{ dur
                  }} in call</span> }
              </div>
              <!-- What they joined FROM. A browser participant's own client-info
                   capture (host at call start, guest at approval); a phone leg
                   has no browser, so its row says so instead of staying blank. -->
              @if (participantDevice(p); as dev) {
              <div class="truncate text-[11px] text-slate-400">{{ dev }}</div>
              }
            </div>
            <span class="text-[11px] capitalize text-slate-400">{{ p.status }}</span>
          </div>
          } @empty {
          <p class="text-xs text-slate-400">No participants recorded.</p>
          }
        </div>
      </div>

      @if (s.security) {
      <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
        <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Host security</h3>
        <app-security-info [info]="s.security"></app-security-info>
      </div>
      }
      }
    </div>

    <!-- ── main: tabbed detail (own scroller on lg) ── -->
    <div class="flex min-w-0 flex-col lg:min-h-0">
      <div
        class="mb-3 flex shrink-0 flex-wrap gap-1 rounded-lg border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-1">
        @for (t of tabs; track t.key) {
        <button type="button" (click)="tab.set(t.key)"
          class="inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors" [class]="tab() === t.key
                  ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 dark:text-ink-soft/60 dark:hover:text-white'">
          {{ t.label }}
          @if (t.key === 'video' && renderStatus(); as rs) {
          <span class="ml-1.5 rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide"
            [class]="renderStatusClass()">{{ rs }}</span>
          }
          @if (t.key === 'costing' && costs(); as c) {
          <span
            class="ml-1.5 rounded-full bg-emerald-50 px-1.5 py-px text-[9px] font-semibold tabular-nums text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">{{
            usd(c.totalUsd) }}</span>
          }
        </button>
        }
      </div>

      <!-- Slides and Video fill the column as fixed panes; every other tab scrolls internally. -->
      <!-- slides / video / synced transcript are fixed panes (internal scroll); the rest scroll here -->
      <div class="lg:min-h-0 lg:flex-1"
        [class]="tab() === 'slides' || tab() === 'video' || tab() === 'insights' || (tab() === 'transcript' && audioSrc()) ? 'flex flex-col' : 'scrollbar-theme lg:overflow-y-auto lg:pr-1'">
        <!-- TRANSCRIPT: the audio-synced player when the call has audio, plain bubbles otherwise -->
        @if (tab() === 'transcript') {
        @if (audioSrc()) {
        <app-transcript-player [report]="report()!" (buildWaveform)="onBuildWaveform()" />
        } @else {
        <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
          <div class="space-y-2.5">
            @for (row of transcriptRows(); track $index; let i = $index) {
            @if (row.dateLabel) {
            <div class="flex justify-center py-1">
              <span
                class="rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-white/10 dark:text-ink-soft/70">{{
                row.dateLabel }}</span>
            </div>
            }
            <div class="flex" [class]="isProspect(row.turn) ? 'justify-start' : 'justify-end'">
              <div class="max-w-[85%] rounded-xl px-3 py-1.5" [class]="isProspect(row.turn)
                          ? 'rounded-bl-sm bg-sky-50 dark:bg-sky-500/10'
                          : 'rounded-br-sm bg-emerald-50 dark:bg-emerald-500/10'">
                <span class="text-[10px] font-semibold uppercase tracking-wide"
                  [class]="isProspect(row.turn) ? 'text-sky-600 dark:text-sky-400' : 'text-emerald-600 dark:text-emerald-400'">{{
                  turnSpeaker(row.turn) }}</span>
                <p class="text-sm leading-snug text-slate-700 dark:text-ink-soft">{{ row.turn.text }}</p>
                @if (proofFor(i); as p) {
                <button type="button" (click)="toggleProof(i)"
                        class="mt-1 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
                        [class]="proofBtnClass(p)">
                  <span class="h-1.5 w-1.5 rounded-full" [class]="proofDotClass(p)"></span>
                  {{ proofOpen().has(i) ? 'hide proof' : 'proof' }}
                </button>
                @if (proofOpen().has(i)) {
                <div class="mt-2 rounded-lg border border-[#EAEAEA] bg-white/70 p-2 text-left text-[11px] leading-snug dark:border-line dark:bg-surface-1/60">
                  <!-- where the data came from -->
                  <div class="font-medium text-slate-600 dark:text-ink-soft">{{ sourceLabel(p) }}</div>
                  @if (p.lookups.length) {
                  <div class="mt-1 flex flex-wrap gap-x-2.5 gap-y-0.5">
                    @for (l of p.lookups; track l.index) {
                    <button type="button" class="text-indigo-600 underline dark:text-indigo-400"
                            (click)="focusRagLookup(l.index)">
                      view retrieval proof #{{ l.index + 1 }}{{ l.exact ? '' : ' (inferred pairing)' }}
                    </button>
                    }
                  </div>
                  }
                  @if (p.detectorFlags.length) {
                  <div class="mt-1 flex flex-wrap gap-1">
                    @for (f of p.detectorFlags; track f) {
                    <span class="rounded bg-amber-100 px-1 py-0.5 font-mono text-[10px] text-amber-700">{{ f }}</span>
                    }
                  </div>
                  }
                  <!-- the judge's reason for this response (when the judge ran) -->
                  @if (p.response; as v) {
                  <div class="mt-1">
                    <span class="font-mono" [class]="v.reason === 'ok' ? 'text-emerald-600' : 'text-rose-600'">{{ v.reason }}</span>
                    <span class="text-slate-400"> · answered: {{ v.answered }}</span>
                    @if (v.scores) {
                    <span class="text-slate-400"> · A{{ v.scores.accuracy ?? '—' }} R{{ v.scores.relevance ?? '—' }}
                      C{{ v.scores.completeness ?? '—' }} G{{ v.scores.grounding ?? '—' }}</span>
                    }
                    @if (v.note) { <div class="text-slate-500">{{ v.note }}</div> }
                    @for (c of v.claims ?? []; track $index) {
                    <div class="mt-0.5">
                      <span class="rounded px-1 py-0.5 text-[10px] font-medium" [class]="claimClass(c.verdict)">{{ c.verdict }}</span>
                      {{ c.claim }}
                      @if (c.chunks.length) { <span class="text-slate-400">(chunks {{ c.chunks.join(', ') }})</span> }
                    </div>
                    }
                  </div>
                  } @else {
                  <div class="mt-1 text-slate-400">No per-response reasoning stored — enable the audit judge
                    (LIVE_CALL_AUDIT_JUDGE_ENABLED=1) and re-run this call's audit.</div>
                  }
                  @for (f of p.failures; track $index) {
                  <div class="mt-1">
                    <span class="rounded px-1 py-0.5 text-[10px] font-medium"
                          [class]="f.severity === 'high' ? 'bg-rose-100 text-rose-700'
                                   : f.severity === 'medium' ? 'bg-amber-100 text-amber-700'
                                   : 'bg-gray-100 text-gray-600'">{{ f.severity }}</span>
                    <span class="font-mono">{{ f.reason }}</span>
                    @if (f.fix?.text) { <span class="text-emerald-700"> — fix: {{ f.fix?.text }}</span> }
                  </div>
                  }
                  <div class="mt-1 text-[10px] text-slate-400">
                    Reconstructed from the tool trail and a judge model — not the agent's actual reasoning.</div>
                </div>
                }
                }
                @if (row.time) {
                <div class="mt-0.5 text-right text-[10px] leading-none tabular-nums text-slate-400 dark:text-ink-soft/50">
                  {{ row.time }}</div>
                }
              </div>
            </div>
            } @empty {
            <p class="py-8 text-center text-sm text-slate-400">No transcript was captured for this session.</p>
            }
          </div>
        </div>
        }
        }

        <!-- RESEARCH & OUTCOME -->
        @if (tab() === 'research') {
        <div class="space-y-3">
          @if (r.research?.brief; as b) {
          <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
            <h3 class="mb-1 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Research brief</h3>
            @if (b.summary) { <p class="text-sm text-slate-700 dark:text-ink-soft">{{ b.summary }}</p> }
            <div class="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500 dark:text-ink-soft/60">
              @if (b.industry) { <span>Industry: {{ b.industry }}</span> }
              @if (b.currentVendor) { <span>Vendor: {{ b.currentVendor }}</span> }
              @if (b.recommendedPlan) { <span>Plan: {{ b.recommendedPlan }}</span> }
            </div>
          </div>
          }
          @if (r.outcome; as o) {
          <div class="overflow-hidden rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1">
            <div class="flex items-center gap-2 border-b border-[#EAEAEA] dark:border-line px-4 py-2.5">
              <span
                class="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
                <app-icon-v2 name="phone-outgoing" [size]="11"></app-icon-v2>
              </span>
              <h3 class="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Call outcome</h3>
            </div>
            <div class="space-y-3 p-4">
              @if (o.summary) {
              <div
                class="text-sm leading-relaxed text-slate-700 dark:text-ink-soft [&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:text-[11px] [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-widest [&_h3]:text-slate-400 [&_h3]:first:mt-0 [&_p]:mb-1.5 [&_p]:last:mb-0 [&_ul]:mt-1 [&_ul]:list-disc [&_ul]:space-y-0.5 [&_ul]:pl-4 [&_li]:mb-0.5 [&_strong]:font-semibold [&_strong]:text-slate-800 dark:[&_strong]:text-white"
                [innerHTML]="o.summary"></div>
              }
              @if (o.recommendedPlan || o.nextStep || o.websiteUrl) {
              <dl class="grid gap-2.5 border-t border-[#EAEAEA] pt-3 dark:border-line sm:grid-cols-2">
                @if (o.recommendedPlan) {
                <div class="rounded-lg bg-slate-50 p-3 dark:bg-white/5">
                  <dt
                    class="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <app-icon-v2 name="target" [size]="11"></app-icon-v2> Recommended plan
                  </dt>
                  <dd class="text-sm leading-snug text-slate-700 dark:text-ink-soft">{{ o.recommendedPlan }}</dd>
                </div>
                }
                @if (o.nextStep) {
                <div class="rounded-lg bg-slate-50 p-3 dark:bg-white/5">
                  <dt
                    class="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <app-icon-v2 name="circle-arrow-right" [size]="11"></app-icon-v2> Next step
                  </dt>
                  <dd class="text-sm leading-snug text-slate-700 dark:text-ink-soft">{{ o.nextStep }}</dd>
                </div>
                }
                @if (o.websiteUrl) {
                <div class="rounded-lg bg-slate-50 p-3 dark:bg-white/5 sm:col-span-2">
                  <dt
                    class="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    <app-icon-v2 name="globe" [size]="11"></app-icon-v2> Website
                  </dt>
                  <dd class="truncate text-sm">
                    <a [href]="o.websiteUrl" target="_blank" rel="noopener noreferrer"
                      class="text-indigo-600 hover:underline dark:text-indigo-400">{{ o.websiteUrl }}</a>
                  </dd>
                </div>
                }
              </dl>
              }
            </div>
          </div>
          }
          @if (topPains().length) {
          <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
            <h3 class="mb-1 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Pain points identified
            </h3>
            <ul class="mt-1 space-y-1">
              @for (p of topPains(); track p) {
              <li class="flex gap-2 text-sm text-slate-700 dark:text-ink-soft"><span class="text-indigo-400">•</span>{{
                p }}</li>
              }
            </ul>
          </div>
          }
          @if (!r.research?.brief && !r.outcome && !topPains().length) {
          <p
            class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 py-8 text-center text-sm text-slate-400">
            No research or outcome was captured for this session.</p>
          }
        </div>
        }

        <!-- SLIDES: carousel — one slide at a time, re-rendered from the same public
                 asset the prospect saw. ← / → keys also navigate. -->
        @if (tab() === 'slides') {
        @if (r.slides.length && currentSlide(); as sl) {
        <div
          class="flex flex-col overflow-hidden rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 lg:min-h-0 lg:flex-1">
          <!-- current slide heading -->
          <div class="flex shrink-0 items-center gap-3 border-b border-[#EAEAEA] dark:border-line px-4 py-2.5">
            <span
              class="shrink-0 rounded-md bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-500 dark:bg-surface-0 dark:text-ink-soft/70 tabular-nums">{{
              slideIdx() + 1 }} / {{ r.slides.length }}</span>
            <span class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium" [class]="slideChip(sl.stage)">{{
              stageLabel(sl.stage) }}</span>
            <div class="min-w-0 flex-1">
              <div class="truncate text-[15px] font-semibold text-slate-800 dark:text-white">{{ sl.title || sl.deckKey
                || sl.tool || '—' }}</div>
              <div class="text-[11px] text-slate-400">{{ sl.tool }}@if (sl.slideIndex) { · slide {{ sl.slideIndex }} }
              </div>
            </div>
            <div class="shrink-0 text-right">
              <div class="text-[11px] tabular-nums text-slate-600 dark:text-ink-soft/80">{{ timeOf(sl.shownAt) }}
              </div>
              @if (offsetOf(sl.shownAt); as off) { <div class="text-[10px] tabular-nums text-slate-400">{{ off }} into
                demo</div> }
            </div>
            @if (slideHref(sl); as href) {
            <a [href]="href" target="_blank" rel="noopener"
              class="shrink-0 rounded-lg border border-[#EAEAEA] dark:border-line px-2 py-1 text-[11px] text-slate-500 dark:text-ink-soft/70 hover:bg-slate-100 dark:hover:bg-line">
              Open ↗
            </a>
            }
          </div>

          <!-- stage + floating arrows: fills the remaining card height (16:9 only on small screens) -->
          <div class="group relative aspect-video w-full lg:min-h-0 lg:flex-1 lg:aspect-auto">
            @if (slideSrc(sl); as src) {
            <!-- 2×-sized frame scaled to 50% — desktop-fidelity render inside the card. -->
            <div class="absolute inset-0 overflow-hidden bg-white dark:bg-surface-0">
              <iframe class="pointer-events-none absolute left-0 top-0 h-[200%] w-[200%] origin-top-left scale-50"
                [src]="src" tabindex="-1" aria-hidden="true"></iframe>
            </div>
            } @else {
            <div
              class="absolute inset-0 grid place-items-center bg-gradient-to-br from-indigo-50/70 via-white to-violet-50/70 dark:from-indigo-500/10 dark:via-surface-0 dark:to-violet-500/10">
              <div class="flex max-w-md flex-col items-center gap-3 px-6 text-center">
                <span
                  class="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-indigo-400 shadow-sm dark:bg-white/5 dark:text-indigo-300">
                  <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
                    stroke-linecap="round">
                    <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9z" />
                    <path d="M19 15l.8 1.9 1.9.8-1.9.8L19 20.4l-.8-1.9-1.9-.8 1.9-.8z" />
                  </svg>
                </span>
                <div class="text-base font-medium text-slate-700 dark:text-ink-soft">{{ sl.title ||
                  stageLabel(sl.stage) }}</div>
                <div class="text-xs text-slate-400">Rendered live by the AI for this prospect — the visual itself
                  isn’t stored.</div>
              </div>
            </div>
            }
            <button type="button" (click)="goSlide(slideIdx() - 1)" [disabled]="slideIdx() === 0" title="Previous slide"
              class="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#EAEAEA] bg-white/90 text-slate-600 shadow-md backdrop-blur transition-opacity hover:bg-white disabled:pointer-events-none disabled:opacity-30 dark:border-line dark:bg-surface-1/90 dark:text-ink-soft dark:hover:bg-line">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button type="button" (click)="goSlide(slideIdx() + 1)" [disabled]="slideIdx() >= r.slides.length - 1"
              title="Next slide"
              class="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#EAEAEA] bg-white/90 text-slate-600 shadow-md backdrop-blur transition-opacity hover:bg-white disabled:pointer-events-none disabled:opacity-30 dark:border-line dark:bg-surface-1/90 dark:text-ink-soft dark:hover:bg-line">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </div>

          <!-- jump pills: number + stage color, current one highlighted -->
          <div
            class="flex shrink-0 flex-wrap items-center justify-center gap-1.5 border-t border-[#EAEAEA] dark:border-line px-4 py-2.5">
            @for (s of r.slides; track $index) {
            <button type="button" (click)="goSlide($index)" [title]="s.title || stageLabel(s.stage)"
              class="flex h-7 min-w-[28px] items-center justify-center rounded-md px-1.5 font-mono text-[11px] transition-colors tabular-nums"
              [class]="$index === slideIdx()
                          ? 'bg-indigo-500 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-surface-0 dark:text-ink-soft/60 dark:hover:bg-line'">
              {{ $index + 1 }}
            </button>
            }
            <span class="ml-2 hidden text-[10px] uppercase tracking-wider text-slate-400 sm:block">← → to
              navigate</span>
          </div>
        </div>
        } @else {
        <p
          class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 py-8 text-center text-sm text-slate-400">
          No slides were shown in this session.</p>
        }
        }

        <!-- TIMELINE -->
        @if (tab() === 'rag') {
        <app-rag-log [report]="report()" [auditFn]="auditRagLookup" [focusIndex]="ragFocus()" />
        }

        @if (tab() === 'audit') {
        <app-call-audit [report]="report()" [rerunFn]="rerunCallAudit"
                        (openLookup)="focusRagLookup($event)" />
        }

        @if (tab() === 'timeline') {
        <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
          <div class="relative space-y-0 pl-1">
            @for (row of timeline(); track $index) {
            <div class="relative flex gap-3 pb-4 last:pb-0">
              <!-- rail -->
              <div class="flex flex-col items-center">
                <span class="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" [class]="eventDotClass(row.e.type)"></span>
                @if (!$last) { <span class="w-px flex-1 bg-slate-200 dark:bg-line"></span> }
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span class="text-sm font-medium text-slate-800 dark:text-ink-soft">{{ eventLabel(row.e.type)
                    }}</span>
                  <span class="font-mono text-[10px] text-slate-400">{{ row.e.type }}</span>
                </div>
                @if (eventDetail(row.e); as detail) {
                <div class="truncate text-[11px] text-slate-400">{{ detail }}</div>
                }
              </div>
              <div class="shrink-0 text-right">
                <div class="text-[11px] tabular-nums text-slate-600 dark:text-ink-soft/80">{{ row.time }}</div>
                <div class="text-[10px] tabular-nums text-slate-400">
                  @if (row.offset) { {{ row.offset }} }
                  @if (row.gap) { <span class="text-slate-500 dark:text-ink-soft/40"> · {{ row.gap }} after
                    prev.</span> }
                </div>
              </div>
            </div>
            } @empty {
            <p class="py-8 text-center text-sm text-slate-400">No lifecycle events were reported for this session.</p>
            }
          </div>
        </div>
        }

        <!-- ACTIONS: everything anyone DID in this demo — host & guest UI actions
             (client-event log) merged with the CRM-admin audit trail. -->
        @if (tab() === 'admin') {
        <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1">
          <!-- role filter: All / Host / Guest / Admin / Observer (with per-role counts) -->
          <div class="flex flex-wrap items-center gap-1 border-b border-slate-100 dark:border-line/60 p-2">
            @for (f of actionFilters; track f.key) {
            <button type="button" (click)="actionFilter.set(f.key)"
              class="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors"
              [class]="actionFilter() === f.key
                  ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800 dark:text-ink-soft/60 dark:hover:text-white'">
              {{ f.label }}
              <span class="tabular-nums opacity-60">{{ f.key === 'all' ? actionRows().length : roleCount(f.key) }}</span>
            </button>
            }
          </div>
          <div class="divide-y divide-slate-100 dark:divide-line/60">
            @for (a of filteredActionRows(); track $index) {
            <div class="flex items-center gap-3 px-4 py-2.5">
              <span class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize"
                [ngClass]="actionRoleBadge(a.role)">{{ a.role }}</span>
              <div class="min-w-0 flex-1">
                <div class="text-sm text-slate-700 dark:text-ink-soft">
                  <span class="font-medium">{{ a.name }}</span>
                  <span> — {{ a.label }}</span>
                </div>
                <div class="text-[11px] text-slate-400">
                  @if (a.detail) { <span>{{ a.detail }}</span> }
                  @if (a.ip) { <span> · from {{ a.ip }}</span> }
                </div>
              </div>
              <div class="shrink-0 text-right">
                <div class="text-[11px] tabular-nums text-slate-600 dark:text-ink-soft/80">{{ timeOf(a.at) }}
                </div>
                @if (offsetOf(a.at); as off) { <div class="text-[10px] tabular-nums text-slate-400">{{ off }}
                  into demo</div> }
              </div>
            </div>
            } @empty {
            <p class="py-8 text-center text-sm text-slate-400">
              {{ actionFilter() === 'all' ? 'No actions recorded for this session.'
              : 'No ' + actionFilter() + ' actions in this session.' }}
            </p>
            }
          </div>
        </div>
        }

        <!-- VIDEO: the server-rendered session replay mp4 (live_call_recordings.video_render_url). -->
        @if (tab() === 'video') {
        @if (videoSrc(); as src) {
        <div
          class="flex flex-col overflow-hidden rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 lg:min-h-0 lg:flex-1">
          <!-- recording heading -->
          <div class="flex shrink-0 items-center gap-3 border-b border-[#EAEAEA] dark:border-line px-4 py-2.5">
            <span
              class="shrink-0 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300">Session
              replay</span>
            @if (renderStatus(); as rs) {
            <span class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
              [class]="renderStatusClass()">{{ rs }}</span>
            }
            <div class="min-w-0 flex-1">
              <div class="truncate text-[15px] font-semibold text-slate-800 dark:text-white">Call recording</div>
              <div class="text-[11px] text-slate-400">Rendered replay — what the prospect saw, with the full call
                audio</div>
            </div>
            @if (videoDuration(); as dur) {
            <div class="shrink-0 text-right">
              <div class="text-[11px] tabular-nums text-slate-600 dark:text-ink-soft/80">{{ dur }}</div>
              <div class="text-[10px] text-slate-400">duration</div>
            </div>
            }
            <a [href]="src" target="_blank" rel="noopener"
              class="shrink-0 rounded-lg border border-[#EAEAEA] dark:border-line px-2 py-1 text-[11px] text-slate-500 dark:text-ink-soft/70 hover:bg-slate-100 dark:hover:bg-line">
              Open ↗
            </a>
          </div>

          <!-- player: fills the remaining card height (16:9 only on small screens) -->
          <div class="relative aspect-video w-full bg-black lg:aspect-auto lg:min-h-0 lg:flex-1">
            <video class="absolute inset-0 h-full w-full" [src]="src" controls preload="metadata" playsinline></video>
          </div>
        </div>
        } @else {
        <div
          class="grid place-items-center rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 py-16 lg:min-h-0 lg:flex-1 lg:py-0">
          <div class="flex max-w-md flex-col items-center gap-3 px-6 text-center">
            @switch (videoState()) {
            @case ('rendering') {
            <span class="h-9 w-9 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"></span>
            <div class="text-base font-medium text-slate-700 dark:text-ink-soft">Video is still rendering</div>
            <div class="text-xs text-slate-400">This session's replay is being stitched into a video on the server.
              Check back in a few minutes.</div>
            }
            @case ('failed') {
            <span
              class="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-400 dark:bg-red-500/10 dark:text-red-300">
              <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </span>
            <div class="text-base font-medium text-slate-700 dark:text-ink-soft">Video render failed</div>
            <div class="text-xs text-slate-400">The replay could not be rendered for this session. The raw recording
              assets are preserved, so the render can be retried.</div>
            }
            @default {
            <span
              class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-ink-soft/50">
              <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
                stroke-linecap="round" stroke-linejoin="round">
                <path
                  d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            </span>
            <div class="text-base font-medium text-slate-700 dark:text-ink-soft">No video recording</div>
            <div class="text-xs text-slate-400">This session wasn't recorded, or no replay video has been generated
              for it.</div>
            }
            }
          </div>
        </div>
        }
        }

        <!-- SESSION INSIGHTS: behavioural signals + click heatmap + interactive replay. -->
        @if (tab() === 'insights') {
        <app-session-insights-panel [recording]="recording()" [events]="report()?.events ?? null" />
        }

        <!-- COSTING: token usage + USD spend per paid API call (live_call_api_costs). -->
        @if (tab() === 'costing') {
        @if (costsError()) {
        <div
          class="grid place-items-center rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 py-16">
          <p class="text-sm text-red-500">Could not load this demo’s cost data.</p>
        </div>
        }
        @if (!costsError() && !costs()) {
        <div
          class="grid place-items-center rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 py-16">
          <span class="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent"></span>
        </div>
        }
        @if (costs(); as c) {
        @if (!c.rows.length) {
        <div
          class="grid place-items-center rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 py-16">
          <div class="flex max-w-md flex-col items-center gap-3 px-6 text-center">
            <span
              class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-ink-soft/50">
              <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
                stroke-linecap="round">
                <path d="M12 1v22" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </span>
            <div class="text-base font-medium text-slate-700 dark:text-ink-soft">No API costs recorded</div>
            <div class="text-xs text-slate-400">No billable API usage was captured for this demo — it may have ended
              before any tokens were spent, or cost tracking was disabled.</div>
          </div>
        </div>
        } @else {
        <div class="space-y-3">
          <!-- summary strip -->
          <div class="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
              <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{
                usd(c.totalUsd) }}</div>
              <div class="text-[11px] text-slate-400">Total cost (USD)</div>
            </div>
            <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
              <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{
                c.rows.length }}</div>
              <div class="text-[11px] text-slate-400">Billed API calls</div>
            </div>
            <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
              <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{
                tokens(tokenTotal('in')) }}</div>
              <div class="text-[11px] text-slate-400">Input tokens</div>
            </div>
            <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 px-4 py-3">
              <div class="text-lg font-semibold leading-5 text-[#141437] dark:text-white tabular-nums">{{
                tokens(tokenTotal('out')) }}</div>
              <div class="text-[11px] text-slate-400">Output tokens</div>
            </div>
          </div>

          @if (c.anyEstimated) {
          <div
            class="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
            <svg class="mt-px h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
            <span>Includes a duration-based <strong>estimate</strong> — the browser never reported live voice token
              usage for this demo, so its voice cost was estimated from the call length.</span>
          </div>
          }

          <!-- by service -->
          <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
            <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Cost by service</h3>
            <div class="overflow-x-auto">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="text-[10px] uppercase tracking-widest text-slate-400">
                    <th class="py-1.5 pr-3 font-semibold">Service</th>
                    <th class="py-1.5 pr-3 text-right font-semibold">Calls</th>
                    <th class="py-1.5 pr-3 text-right font-semibold">Input tokens</th>
                    <th class="py-1.5 pr-3 text-right font-semibold">Output tokens</th>
                    <th class="py-1.5 text-right font-semibold">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  @for (s of c.byService; track s.service) {
                  <tr class="border-t border-slate-100 dark:border-line/60">
                    <td class="py-2 pr-3 text-slate-700 dark:text-ink-soft">{{ serviceLabel(s.service) }}</td>
                    <td class="py-2 pr-3 text-right tabular-nums text-slate-600 dark:text-ink-soft/80">{{ s.calls }}
                    </td>
                    <td class="py-2 pr-3 text-right tabular-nums text-slate-600 dark:text-ink-soft/80">{{
                      tokens(s.inputTokens) }}</td>
                    <td class="py-2 pr-3 text-right tabular-nums text-slate-600 dark:text-ink-soft/80">{{
                      tokens(s.outputTokens) }}</td>
                    <td class="py-2 text-right font-medium tabular-nums text-slate-800 dark:text-white">{{
                      usd(s.costUsd) }}</td>
                  </tr>
                  }
                  <tr class="border-t border-slate-200 dark:border-line">
                    <td class="py-2 pr-3 font-semibold text-slate-800 dark:text-white">Total</td>
                    <td class="py-2 pr-3 text-right tabular-nums font-semibold text-slate-800 dark:text-white">{{
                      c.rows.length }}</td>
                    <td class="py-2 pr-3 text-right tabular-nums font-semibold text-slate-800 dark:text-white">{{
                      tokens(tokenTotal('in')) }}</td>
                    <td class="py-2 pr-3 text-right tabular-nums font-semibold text-slate-800 dark:text-white">{{
                      tokens(tokenTotal('out')) }}</td>
                    <td class="py-2 text-right font-semibold tabular-nums text-emerald-600 dark:text-emerald-300">{{
                      usd(c.totalUsd) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- by operation -->
          <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
            <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Cost by operation</h3>
            <div class="overflow-x-auto">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="text-[10px] uppercase tracking-widest text-slate-400">
                    <th class="py-1.5 pr-3 font-semibold">Operation</th>
                    <th class="py-1.5 pr-3 font-semibold">Service</th>
                    <th class="py-1.5 pr-3 text-right font-semibold">Calls</th>
                    <th class="py-1.5 text-right font-semibold">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  @for (op of c.byOperation; track (op.service + op.operation)) {
                  <tr class="border-t border-slate-100 dark:border-line/60">
                    <td class="py-2 pr-3 capitalize text-slate-700 dark:text-ink-soft">{{ opLabel(op.operation) }}
                    </td>
                    <td class="py-2 pr-3 text-xs text-slate-500 dark:text-ink-soft/60">{{ serviceLabel(op.service) }}
                    </td>
                    <td class="py-2 pr-3 text-right tabular-nums text-slate-600 dark:text-ink-soft/80">{{ op.calls }}
                    </td>
                    <td class="py-2 text-right font-medium tabular-nums text-slate-800 dark:text-white">{{
                      usd(op.costUsd) }}</td>
                  </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>

          <!-- every billed call -->
          <div class="rounded-xl border border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-4">
            <h3 class="mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Billed API calls</h3>
            <div class="overflow-x-auto">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr class="text-[10px] uppercase tracking-widest text-slate-400">
                    <th class="py-1.5 pr-3 font-semibold">Time</th>
                    <th class="py-1.5 pr-3 font-semibold">Operation</th>
                    <th class="py-1.5 pr-3 font-semibold">Model</th>
                    <th class="py-1.5 pr-3 text-right font-semibold">Input</th>
                    <th class="py-1.5 pr-3 text-right font-semibold">Output</th>
                    <th class="py-1.5 text-right font-semibold">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of c.rows; track row.id) {
                  <tr class="border-t border-slate-100 dark:border-line/60 align-top">
                    <td class="whitespace-nowrap py-2 pr-3">
                      <div class="tabular-nums text-slate-600 dark:text-ink-soft/80">{{ timeOf(row.created_at) }}
                      </div>
                      @if (offsetOf(row.created_at); as off) { <div class="text-[10px] tabular-nums text-slate-400">{{
                        off }}</div> }
                    </td>
                    <td class="py-2 pr-3">
                      <div class="flex flex-wrap items-center gap-1.5">
                        <span class="capitalize text-slate-700 dark:text-ink-soft">{{ opLabel(row.operation)
                          }}</span>
                        <span class="rounded px-1 py-px text-[9px] font-semibold uppercase" [class]="row.service === 'gemini_live'
                                      ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300'
                                      : 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300'">{{
                          row.service }}</span>
                        @if (row.estimated) {
                        <span
                          class="rounded bg-amber-50 px-1 py-px text-[9px] font-semibold uppercase text-amber-600 dark:bg-amber-500/10 dark:text-amber-300">estimated</span>
                        }
                      </div>
                      @if (rowExtras(row); as extras) { <div class="text-[10px] tabular-nums text-slate-400">{{ extras
                        }}</div> }
                    </td>
                    <td class="py-2 pr-3 font-mono text-[11px] text-slate-500 dark:text-ink-soft/60">{{ row.model ||
                      '—' }}</td>
                    <td class="py-2 pr-3 text-right tabular-nums text-slate-600 dark:text-ink-soft/80">{{
                      tokens(row.input_tokens) }}</td>
                    <td class="py-2 pr-3 text-right tabular-nums text-slate-600 dark:text-ink-soft/80">{{
                      tokens(row.output_tokens) }}</td>
                    <td class="py-2 text-right font-medium tabular-nums text-slate-800 dark:text-white">{{
                      usd(row.cost_usd) }}</td>
                  </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
        }
        }
        }
      </div>
    </div>
  </div>
</div>
}
</div>